import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Badge, EmptyState, IconButton, Page, PageHeader, Segmented, TableSkeleton } from "../../../components/ui";
import { createIncident, getHealth, getIncidents, savePostmortem, updateIncident } from "../../../services/incidents";
import { can } from "../../../utils/roles";
import { PROTOCOL, RUNBOOKS, SEVERITY_GUIDE } from "./runbooks";
import "../payments-ledger/payments.css";
import "./incidents.css";

const STATE = {
  ok: { label: "Operational", color: "green" },
  degraded: { label: "Degraded", color: "amber" },
  down: { label: "Down", color: "red" },
  off: { label: "Not configured", color: "slate" },
};
const SEVERITY = { sev1: { label: "SEV1", color: "red" }, sev2: { label: "SEV2", color: "amber" }, sev3: { label: "SEV3", color: "slate" } };
const STATUS = {
  investigating: "Investigating",
  identified: "Cause identified",
  monitoring: "Fixed, monitoring",
  resolved: "Resolved",
};
const COMPONENTS = ["api", "database", "jwplayer", "stripe", "pawapay", "cinetpay", "firebase", "mailgun", "site", "app_android", "app_ios", "backoffice", "other"];
const COMPONENT_LABELS = {
  api: "API",
  database: "Database",
  jwplayer: "JW Player",
  stripe: "Stripe",
  pawapay: "pawaPay",
  cinetpay: "CinetPay",
  firebase: "Firebase",
  mailgun: "Mailgun",
  site: "Website",
  app_android: "Android app",
  app_ios: "iPhone app",
  backoffice: "Back-office",
  other: "Other",
};
const when = (date) => (date ? new Date(date).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const duration = (minutes, t) => (minutes < 60 ? t("{{count}} min", { count: minutes }) : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")}`);

const muted = { colors: "#9b9ba6" };
const minuteLabel = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

/**
 * Last 15 minutes of the API: successful requests and errors (stacked columns), response time p95
 * (line, right axis, red above 1.5 s).
 */
export const trafficChart = (minutes, t) => {
  const series = [
    { name: t("Requests"), type: "column", data: minutes.map((m) => Math.max(0, m.requests - m.errors)) },
    { name: t("Errors"), type: "column", data: minutes.map((m) => m.errors) },
    { name: t("Response time p95"), type: "line", data: minutes.map((m) => m.p95) },
  ];
  return {
    series,
    options: {
      chart: { id: "incident-traffic", stacked: true, background: "transparent", toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, fontFamily: "inherit" },
      colors: ["#1176BC", "#ef5d5d", "#CDAE6E"],
      stroke: { width: [0, 0, 3], curve: "smooth" },
      markers: { size: 3, strokeWidth: 0 },
      plotOptions: { bar: { columnWidth: "60%", borderRadius: 2 } },
      dataLabels: { enabled: false },
      labels: minutes.map((m) => m.at),
      grid: { borderColor: "rgba(255, 255, 255, 0.06)", strokeDashArray: 4 },
      legend: { position: "top", horizontalAlign: "right", labels: muted },
      xaxis: {
        type: "category",
        labels: { style: muted, rotate: 0, hideOverlappingLabels: true, formatter: (value) => (value ? minuteLabel(value) : "") },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: false },
      },
      yaxis: [
        { seriesName: t("Requests"), min: 0, forceNiceScale: true, title: { text: t("per minute"), style: { color: "#9b9ba6", fontWeight: 400 } }, labels: { style: muted, formatter: (v) => Math.round(v).toLocaleString() } },
        { seriesName: t("Requests"), show: false },
        { seriesName: t("Response time p95"), opposite: true, min: 0, forceNiceScale: true, labels: { style: muted, formatter: (v) => (v == null ? "" : `${Math.round(v)} ms`) } },
      ],
      annotations: {
        yaxis: [{ y: 1500, yAxisIndex: 2, borderColor: "#ef5d5d", strokeDashArray: 4, label: { text: t("slow"), style: { color: "#fff", background: "#ef5d5d" } } }],
      },
      tooltip: {
        theme: "dark",
        shared: true,
        intersect: false,
        x: { formatter: (v, { dataPointIndex }) => (minutes[dataPointIndex] ? minuteLabel(minutes[dataPointIndex].at) : "") },
        y: { formatter: (v, { seriesIndex }) => (v == null ? "—" : seriesIndex === 2 ? `${Math.round(v)} ms` : Math.round(v).toLocaleString()) },
      },
    },
  };
};

/** Live health: one card per component, API figures, requests per minute. */
const Health = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback((force = false) => {
    setError(false);
    getHealth(force)
      .then(setData)
      .catch(() => setError(true));
  }, []);
  useEffect(() => {
    load();
    const timer = setInterval(() => load(), 30000);
    return () => clearInterval(timer);
  }, [load]);

  if (error) {
    return (
      <EmptyState icon="ri-error-warning-line">
        {t("The API does not answer: it may be down. Follow the « API » procedure.")}{" "}
        <button type="button" className="ui-btn ui-btn-secondary" onClick={() => load(true)}>
          {t("Retry")}
        </button>
      </EmptyState>
    );
  }
  if (!data) return <TableSkeleton rows={6} />;
  const api = data.api;
  return (
    <>
      <div className="incident-overall" data-state={data.overall}>
        <Badge color={STATE[data.overall].color} dot>
          {t(STATE[data.overall].label)}
        </Badge>
        {data.busy && <Badge color="amber">{t("Affluence mode is on")}</Badge>}
        <span className="ui-hint">{t("Checked {{date}} · refreshed every 30 s", { date: when(data.at) })}</span>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={() => load(true)}>
          <i className="ri-refresh-line" aria-hidden="true"></i>
          {t("Check everything now")}
        </button>
      </div>
      <div className="incident-components">
        {Object.entries(data.components).map(([key, c]) => (
          <section key={key} className="ui-card incident-component" data-state={c.status}>
            <div className="incident-component-head">
              <strong>{t(c.label)}</strong>
              <Badge color={STATE[c.status]?.color || "slate"} dot>
                {t(STATE[c.status]?.label || c.status)}
              </Badge>
            </div>
            <span className="ui-hint">{c.detail}</span>
          </section>
        ))}
      </div>
      <div className="ledger-stats">
        {[
          [api.requestsPerMinute, t("Requests per minute (5 min)")],
          [api.p95 !== null ? `${api.p95} ms` : "—", t("Response time p95 (15 min)")],
          [`${String(api.errorRate).replace(".", ",")} %`, t("Server errors")],
          [`${api.eventLoopLagMs} ms`, t("Event loop delay")],
          [`${api.memoryMb} Mo`, api.processes > 1 ? t("API memory ({{count}} processes)", { count: api.processes }) : t("API memory")],
          [duration(api.uptimeMinutes, t), t("Up since")],
        ].map(([value, label]) => (
          <div key={label} className="ui-card ledger-stat">
            <strong>{value}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
      <section className="ui-card incident-chart">
        <h3>{t("Requests per minute (15 min)")}</h3>
        {(() => {
          const chart = trafficChart(api.minutes, t);
          return <Chart type="line" height={260} options={chart.options} series={chart.series} />;
        })()}
        <p className="ui-hint">
          {api.processes > 1
            ? t("Total of the {{count}} API processes (cluster mode). The videos themselves are served by JW Player and do not go through the API.", { count: api.processes })
            : t("Measured by this API process since its last restart. The videos themselves are served by JW Player and do not go through the API.")}
        </p>
      </section>
    </>
  );
};

/** Declare an incident. */
const DeclareModal = ({ onClose, onCreated }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ title: "", severity: "sev2", components: [], impact: "", note: "" });
  const [busy, setBusy] = useState(false);
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { incident } = await createIncident(form);
      toast.success(t("Incident declared"));
      onCreated(incident);
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
      setBusy(false);
    }
  };
  return (
    <Modal show={true} onHide={onClose} centered size="lg" dialogClassName="ui-modal">
      <form onSubmit={submit}>
        <Modal.Header>
          <Modal.Title>{t("Declare an incident")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="refund-form">
          <label className="refund-field">
            <span>{t("What is happening?")}</span>
            <input className="ui-input" value={form.title} maxLength={160} onChange={(e) => set({ title: e.target.value })} placeholder={t("e.g. Videos do not play on the app")} required />
          </label>
          <div className="refund-field">
            <span>{t("Severity")}</span>
            <Segmented label={t("Severity")} value={form.severity} onChange={(severity) => set({ severity })} options={SEVERITY_GUIDE.map((s) => ({ value: s.level, label: s.title }))} />
            <small className="ui-hint">{SEVERITY_GUIDE.find((s) => s.level === form.severity)?.when}</small>
          </div>
          <div className="refund-field">
            <span>{t("Affected")}</span>
            <div className="ui-chips">
              {COMPONENTS.map((c) => (
                <button key={c} type="button" className="ui-chip" aria-pressed={form.components.includes(c)} onClick={() => set({ components: form.components.includes(c) ? form.components.filter((x) => x !== c) : [...form.components, c] })}>
                  {t(COMPONENT_LABELS[c])}
                </button>
              ))}
            </div>
          </div>
          <label className="refund-field">
            <span>{t("Impact for the users")}</span>
            <input className="ui-input" value={form.impact} maxLength={1000} onChange={(e) => set({ impact: e.target.value })} placeholder={t("Who is affected, since when, what they see")} />
          </label>
          <label className="refund-field">
            <span>{t("First note")}</span>
            <textarea className="ui-input ledger-textarea" rows={3} value={form.note} maxLength={2000} onChange={(e) => set({ note: e.target.value })} />
          </label>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button type="submit" className="ui-btn ui-btn-danger" disabled={busy || !form.title.trim()}>
            {t("Declare")}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

/** One incident: timeline, update (note + status), ownership, post-mortem. */
const IncidentDetail = ({ incident, canEdit, onClose, onChange }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [pm, setPm] = useState({ cause: incident.postmortem?.cause || "", fix: incident.postmortem?.fix || "", actions: (incident.postmortem?.actions || []).join("\n") });
  const run = async (fn, success) => {
    setBusy(true);
    try {
      const { incident: updated } = await fn();
      toast.success(t(success));
      setNote("");
      setStatus("");
      onChange(updated);
    } catch (error) {
      toast.error(error?.response?.data?.message || t("Error updating data"));
    } finally {
      setBusy(false);
    }
  };
  const resolved = incident.status === "resolved";
  return (
    <Modal show={true} onHide={onClose} centered size="lg" dialogClassName="ui-modal">
      <Modal.Header>
        <Modal.Title>
          <Badge color={SEVERITY[incident.severity].color}>{SEVERITY[incident.severity].label}</Badge> {incident.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="incident-detail">
        <p className="ui-hint">
          {t(STATUS[incident.status])} · {t("started {{date}}", { date: when(incident.startedAt) })} · {duration(incident.durationMinutes, t)}
          {incident.ownerName ? ` · ${t("handled by {{name}}", { name: incident.ownerName })}` : ` · ${t("nobody in charge yet")}`}
        </p>
        {incident.impact && <p>{incident.impact}</p>}
        <ul className="incident-timeline">
          {incident.timeline.map((item, i) => (
            <li key={`${item.at}-${i}`}>
              <span className="ui-hint">{when(item.at)} · {item.by}</span>
              {item.status && <Badge color={item.status === "resolved" ? "green" : "blue"}>{t(STATUS[item.status])}</Badge>}
              <span>{item.note}</span>
            </li>
          ))}
        </ul>

        {canEdit && !resolved && (
          <div className="incident-update">
            <textarea className="ui-input ledger-textarea" rows={3} value={note} maxLength={2000} placeholder={t("What was checked or done, what is next")} onChange={(e) => setNote(e.target.value)} aria-label={t("Update")} />
            <div className="incident-update-actions">
              <select className="ui-input" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("New status")}>
                <option value="">{t("Status unchanged")}</option>
                {Object.entries(STATUS)
                  .filter(([key]) => key !== incident.status)
                  .map(([key, label]) => (
                    <option key={key} value={key}>
                      {t(label)}
                    </option>
                  ))}
              </select>
              {!incident.ownerName && (
                <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => run(() => updateIncident(incident.id, { takeOwnership: true, note: "Prise en charge" }), "You are in charge")}>
                  {t("I'm in charge")}
                </button>
              )}
              <button type="button" className="ui-btn" disabled={busy || (!note.trim() && !status)} onClick={() => run(() => updateIncident(incident.id, { note, status: status || undefined }), "Incident updated")}>
                {t("Add the update")}
              </button>
            </div>
          </div>
        )}

        {resolved && (
          <section className="incident-postmortem">
            <h3>{t("Post-mortem")}</h3>
            {canEdit ? (
              <>
                <label className="refund-field">
                  <span>{t("Cause")}</span>
                  <textarea className="ui-input ledger-textarea" rows={2} value={pm.cause} onChange={(e) => setPm({ ...pm, cause: e.target.value })} />
                </label>
                <label className="refund-field">
                  <span>{t("Fix")}</span>
                  <textarea className="ui-input ledger-textarea" rows={2} value={pm.fix} onChange={(e) => setPm({ ...pm, fix: e.target.value })} />
                </label>
                <label className="refund-field">
                  <span>{t("Actions so it does not happen again (one per line)")}</span>
                  <textarea className="ui-input ledger-textarea" rows={3} value={pm.actions} onChange={(e) => setPm({ ...pm, actions: e.target.value })} />
                </label>
                <div className="incident-update-actions">
                  <button type="button" className="ui-btn ui-btn-secondary" disabled={busy} onClick={() => run(() => updateIncident(incident.id, { status: "investigating", note: note || "Rouvert" }), "Incident reopened")}>
                    {t("Reopen")}
                  </button>
                  <button type="button" className="ui-btn" disabled={busy || !pm.cause.trim() || !pm.fix.trim()} onClick={() => run(() => savePostmortem(incident.id, { cause: pm.cause, fix: pm.fix, actions: pm.actions.split("\n") }), "Post-mortem saved")}>
                    {t("Save the post-mortem")}
                  </button>
                </div>
              </>
            ) : incident.postmortem?.cause ? (
              <p>{incident.postmortem.cause}</p>
            ) : (
              <p className="ui-hint">{t("Not written yet.")}</p>
            )}
          </section>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
          {t("Close")}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const IncidentList = () => {
  const { t } = useTranslation();
  const canEdit = can("logs", "edit");
  const [status, setStatus] = useState("open");
  const [data, setData] = useState(null);
  const [declaring, setDeclaring] = useState(false);
  const [opened, setOpened] = useState(null);
  const load = useCallback(() => {
    setData(null);
    getIncidents(status)
      .then(setData)
      .catch(() => setData({ items: [], open: 0, stats90: {} }));
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <div className="ledger-stats">
        <div className="ui-card ledger-stat" data-tone={data?.open ? "amber" : undefined}>
          <strong>{data ? data.open : "—"}</strong>
          <span>{t("Open incidents")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{data?.stats90?.resolved ?? "—"}</strong>
          <span>{t("Resolved in 90 days")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{data?.stats90?.mttrMinutes != null ? duration(data.stats90.mttrMinutes, t) : "—"}</strong>
          <span>{t("Average time to resolve")}</span>
        </div>
        <div className="ui-card ledger-stat">
          <strong>{data?.stats90?.sev1 ?? "—"}</strong>
          <span>{t("SEV1 in 90 days")}</span>
        </div>
      </div>
      <section className="ui-card">
        <div className="ui-toolbar ledger-toolbar">
          <Segmented
            label={t("status")}
            value={status}
            onChange={setStatus}
            options={[
              { value: "open", label: t("In progress") },
              { value: "resolved", label: t("Resolved") },
              { value: "all", label: t("All") },
            ]}
          />
          {canEdit && (
            <button type="button" className="ui-btn ui-btn-danger" onClick={() => setDeclaring(true)}>
              <i className="ri-alarm-warning-line" aria-hidden="true"></i>
              {t("Declare an incident")}
            </button>
          )}
        </div>
        {!data ? (
          <TableSkeleton />
        ) : !data.items.length ? (
          <EmptyState icon="ri-checkbox-circle-line">{status === "open" ? t("No incident in progress") : t("No incident")}</EmptyState>
        ) : (
          <ul className="incident-list">
            {data.items.map((item) => (
              <li key={item.id}>
                <button type="button" className="incident-row" onClick={() => setOpened(item)}>
                  <Badge color={SEVERITY[item.severity].color}>{SEVERITY[item.severity].label}</Badge>
                  <span className="incident-row-title">
                    <strong>{item.title}</strong>
                    <span className="ui-hint">
                      {[t(STATUS[item.status]), when(item.startedAt), duration(item.durationMinutes, t), item.ownerName || t("nobody in charge yet"), item.source === "alert" ? t("opened by an alert") : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  <span className="incident-row-components">
                    {item.components.map((c) => (
                      <Badge key={c} color="slate">
                        {t(COMPONENT_LABELS[c] || c)}
                      </Badge>
                    ))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
      {declaring && (
        <DeclareModal
          onClose={() => setDeclaring(false)}
          onCreated={(incident) => {
            setDeclaring(false);
            setOpened(incident);
            load();
          }}
        />
      )}
      {opened && (
        <IncidentDetail
          incident={opened}
          canEdit={canEdit}
          onClose={() => setOpened(null)}
          onChange={(updated) => {
            setOpened(updated);
            load();
          }}
        />
      )}
    </>
  );
};

/** Severity levels, the protocol to follow and one procedure per component (in French). */
const Procedures = () => {
  const { t } = useTranslation();
  return (
    <>
      <section className="ui-card incident-doc">
        <h3>{t("Severity")}</h3>
        <ul className="incident-severity">
          {SEVERITY_GUIDE.map((s) => (
            <li key={s.level}>
              <Badge color={SEVERITY[s.level].color}>{SEVERITY[s.level].label}</Badge>
              <div>
                <strong>{s.title}</strong>
                <p>{s.when}</p>
                <p className="ui-hint">{s.response}</p>
              </div>
            </li>
          ))}
        </ul>
        <h3>{t("Protocol")}</h3>
        <ol className="incident-steps">
          {PROTOCOL.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>
      {RUNBOOKS.map((r) => (
        <details key={r.id} className="ui-card incident-doc incident-runbook">
          <summary>
            <strong>{r.title}</strong>
          </summary>
          <p className="ui-hint">{r.signs}</p>
          <ol className="incident-steps">
            {r.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </details>
      ))}
    </>
  );
};

/**
 * Incidents: live health of the platform, incidents (declare, follow, close
 * with a post-mortem) and the procedures for each kind of outage.
 */
const Incidents = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("health");
  return (
    <Page>
      <PageHeader
        title={t("Incidents")}
        subtitle={t("Health of the platform, incidents in progress and what to do when something breaks.")}
        actions={<IconButton icon="ri-question-line" label={t("Procedures")} onClick={() => setTab("procedures")} />}
      />
      <div className="incident-tabs">
        <Segmented
          label={t("Incidents")}
          value={tab}
          onChange={setTab}
          options={[
            { value: "health", label: t("Health") },
            { value: "incidents", label: t("Incidents") },
            { value: "procedures", label: t("Procedures") },
          ]}
        />
      </div>
      {tab === "health" && <Health />}
      {tab === "incidents" && <IncidentList />}
      {tab === "procedures" && <Procedures />}
    </Page>
  );
};

export default Incidents;
